/**
 * MessengerChat.js — EcoScienceInnova
 * Full-page 3-panel messenger: Room-based architecture (Direct & Group)
 * Real-time polling, new conversation modal, member invitation.
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import chatService from '../services/chatService';
import userService from '../services/userService';
import ResearcherProfileView from './ResearcherProfileView';
import Nav from './Nav';
import {
  FaSearch, FaPaperPlane, FaSmile, FaPaperclip, FaVideo, FaPhone,
  FaEllipsisV, FaUser, FaBellSlash, FaEnvelope, FaMapMarkerAlt,
  FaBan, FaEdit, FaCheckDouble, FaCheck, FaComments, FaFile,
  FaTimes, FaUserPlus, FaCircle, FaUsers, FaArrowLeft, FaTrash
} from 'react-icons/fa';
import EmojiPicker, { Theme } from 'emoji-picker-react';
import { JitsiMeeting } from '@jitsi/react-sdk';
import './MessengerChat.css';

const AVATAR_COLORS = [
  '#FF6600', '#3b82f6', '#22c55e', '#a855f7',
  '#ec4899', '#f59e0b', '#0ea5e9', '#14b8a6',
];

const getAvatarColor = (name) => {
  const idx = (name?.charCodeAt(0) || 0) % AVATAR_COLORS.length;
  return AVATAR_COLORS[idx];
};

const getInitials = (name) => {
  return (name || '?')
    .split(' ')
    .map(w => w[0] || '')
    .slice(0, 2)
    .join('')
    .toUpperCase();
};

const getBackendUrl = () => {
  let url = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';
  if (url.endsWith('/api')) url = url.slice(0, -4);
  return url;
};

const formatTime = (dateStr) => {
  const d = new Date(dateStr);
  return d.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
};

const formatDate = (dateStr) => {
  const d = new Date(dateStr);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  if (d.toDateString() === today.toDateString()) return 'Hoy';
  if (d.toDateString() === yesterday.toDateString()) return 'Ayer';
  return d.toLocaleDateString('es-ES', { weekday: 'short', day: 'numeric', month: 'short' });
};

const formatConvTime = (dateStr) => {
  const d = new Date(dateStr);
  const today = new Date();
  if (d.toDateString() === today.toDateString()) return formatTime(dateStr);
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  if (d.toDateString() === yesterday.toDateString()) return 'Ayer';
  return d.toLocaleDateString('es-ES', { day: 'numeric', month: 'short' });
};

const isOnlyEmojis = (str) => {
  if (!str) return false;
  const clean = str.replace(/[\s\n]/g, '');
  if (!clean) return false;
  try {
    const emojiRegex = /^(\p{Emoji_Presentation}|\p{Emoji}\uFE0F)+$/u;
    return emojiRegex.test(clean);
  } catch (e) {
    // Fallback if environment doesn't support unicode property escapes
    const fallbackRegex = /^([\u2700-\u27bf]|[\u1f300-\u1f64f]|[\u1f680-\u1f6ff]|[\u1f900-\u1f9ff]|[\u2600-\u26ff]|\u200d)+$/u;
    return fallbackRegex.test(clean);
  }
};


const Avatar = ({ name, photoUrl, size = 44, className = '' }) => {
  const backendUrl = getBackendUrl();
  const [imgError, setImgError] = useState(false);

  const initials = getInitials(name);
  const bgColor = getAvatarColor(name);

  const containerStyle = {
    width: size,
    height: size,
    backgroundColor: bgColor,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '50%',
    overflow: 'hidden',
    flexShrink: 0,
    fontSize: `${size * 0.4}px`,
    color: '#fff',
    fontWeight: 'bold',
    textTransform: 'uppercase'
  };

  if (photoUrl && !imgError) {
    const src = photoUrl.startsWith('http') ? photoUrl : `${backendUrl}${photoUrl.startsWith('/') ? '' : '/'}${photoUrl}`;
    return (
      <div className={`mc-avatar-container ${className}`} style={containerStyle}>
        <img
          src={src}
          alt={name}
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          onError={() => setImgError(true)}
        />
      </div>
    );
  }

  return (
    <div className={`mc-avatar-container ${className}`} style={containerStyle}>
      {initials}
    </div>
  );
};

// ── New Conversation / Invite Modal ──────────────────────────────
const ContactModal = ({ contacts, onSelect, onClose, title = "Nueva conversación", excludeIds = [] }) => {
  const [search, setSearch] = useState('');
  const filtered = contacts.filter(c =>
    !excludeIds.includes(c.id) &&
    (c.name.toLowerCase().includes(search.toLowerCase()) ||
      (c.role && c.role.toLowerCase().includes(search.toLowerCase())))
  );

  return (
    <div className="mc-modal-overlay" onClick={onClose}>
      <div className="mc-modal" onClick={e => e.stopPropagation()}>
        <div className="mc-modal-header">
          <h3>{title}</h3>
          <button className="mc-modal-close" onClick={onClose}><FaTimes /></button>
        </div>

        <div className="mc-modal-search">
          <FaSearch className="mc-modal-search-icon" />
          <input
            type="text"
            placeholder="Buscar contacto..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            autoFocus
          />
        </div>

        <p className="mc-modal-subtitle">Tus contactos</p>

        <div className="mc-modal-list">
          {filtered.length === 0 ? (
            <div className="mc-modal-empty">
              <p>No se encontraron contactos</p>
            </div>
          ) : (
            filtered.map(c => (
              <div
                key={c.id}
                className="mc-modal-contact"
                onClick={() => onSelect(c)}
              >
                <Avatar name={c.name} photoUrl={c.photoUrl} size={42} className="mc-avatar" />
                <div className="mc-modal-contact-info">
                  <p className="mc-modal-contact-name">{c.name}</p>
                  <p className="mc-modal-contact-role">{c.role}</p>
                </div>
                <FaCircle className="mc-modal-online-dot" />
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

// ── Main Component ──────────────────────────────────────────────
const MessengerChat = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preselectedUserId = searchParams.get('userId');

  const [rooms, setRooms] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [activeRoom, setActiveRoom] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [modalType, setModalType] = useState(null); // 'NEW' or 'INVITE'
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [messageToDelete, setMessageToDelete] = useState(null);
  const [deleteError, setDeleteError] = useState(null);
  const [showLeaveModal, setShowLeaveModal] = useState(false);
  const [isVideoCallActive, setIsVideoCallActive] = useState(false);
  const fileInputRef = useRef(null);

  const [viewingProfile, setViewingProfile] = useState(null);
  const [loadingProfile, setLoadingProfile] = useState(false);

  const messagesEndRef = useRef(null);
  const pollRef = useRef(null);
  const roomPollRef = useRef(null);
  const activeRoomRef = useRef(null);
  const emojiPickerRef = useRef(null);
  const didAutoSelect = useRef(false);

  useEffect(() => { activeRoomRef.current = activeRoom; }, [activeRoom]);

  useEffect(() => {
    loadData();
    roomPollRef.current = setInterval(async () => {
      try {
        const data = await chatService.getConversations(token);
        setRooms(data);
      } catch { /* silent */ }
    }, 8000);

    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
      if (roomPollRef.current) clearInterval(roomPollRef.current);
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (emojiPickerRef.current && !emojiPickerRef.current.contains(event.target)) {
        setShowEmojiPicker(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (contacts.length === 0) return;
    if (didAutoSelect.current) return;

    if (preselectedUserId) {
      const targetId = parseInt(preselectedUserId);
      const contact = contacts.find(c => c.id === targetId);
      if (contact) {
        didAutoSelect.current = true;
        handleSelectUser(contact);
        return;
      }
    }

    if (rooms.length > 0) {
      didAutoSelect.current = true;
      const firstUnread = rooms.find(r => r.unreadCount > 0) || rooms[0];
      handleSelectRoom(firstUnread);
    } else if (!loading) {
      didAutoSelect.current = true;
    }
  }, [preselectedUserId, contacts, rooms, loading]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [convData, contactData] = await Promise.all([
        chatService.getConversations(token),
        userService.getContacts(token),
      ]);

      setRooms(convData);

      const contactMap = new Map();
      contactData.forEach(c => {
        const name = `${c.profile?.firstName || ''} ${c.profile?.lastName || ''}`.trim() || 'Investigador';
        contactMap.set(c.id, {
          id: c.id,
          name,
          role: c.professionalProfile?.profession || 'Investigador',
          email: c.email,
          country: c.profile?.country || '',
          institution: c.professionalProfile?.institution || '',
          photoUrl: c.profile?.photoUrl || null,
        });
      });
      setContacts(Array.from(contactMap.values()));
    } catch (err) {
      console.error('Failed to load chat data', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectRoom = async (room) => {
    setActiveRoom(room);
    try {
      const msgs = await chatService.getMessagesByRoom(room.roomId, token);
      setMessages(msgs);
      startPolling(room.roomId);
    } catch (err) {
      console.error('Failed to load messages', err);
    }
  };

  const handleSelectUser = async (targetUser) => {
    try {
      const data = await chatService.getMessagesByUser(targetUser.id, token);
      // Backend returns { roomId, messages }
      const roomObj = {
        roomId: data.roomId,
        type: 'DIRECT',
        user: targetUser,
        members: [{ id: user.id }, targetUser],
        unreadCount: 0
      };
      setActiveRoom(roomObj);
      setMessages(data.messages);
      startPolling(data.roomId);
    } catch (err) {
      console.error('Failed to init direct chat', err);
    }
  };

  const startPolling = (roomId) => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const current = activeRoomRef.current;
        if (current && current.roomId === roomId) {
          const fresh = await chatService.getMessagesByRoom(roomId, token);
          setMessages(prev => (fresh.length !== prev.length ? fresh : prev));
        }
      } catch { /* silent */ }
    }, 3000);
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      setSelectedFile(e.target.files[0]);
    }
  };

  const clearFile = () => {
    setSelectedFile(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleSend = async () => {
    if ((!input.trim() && !selectedFile) || !activeRoom) return;
    const content = input.trim();
    const roomId = activeRoom.roomId;
    const fileToSend = selectedFile;

    setInput('');

    // Optimistic
    const tempMsg = {
      id: `temp-${Date.now()}`,
      content: content || (fileToSend ? '📎 Archivo adjunto' : ''),
      sender: { id: user?.id, profile: user?.profile },
      createdAt: new Date().toISOString(),
      fileUrl: fileToSend ? 'temp' : null,
      fileName: fileToSend?.name,
      fileType: fileToSend?.type
    };
    setMessages(prev => [...prev, tempMsg]);

    try {
      await chatService.sendMessage({ roomId, content, file: fileToSend }, token);
      clearFile();
      const [msgs, roomData] = await Promise.all([
        chatService.getMessagesByRoom(roomId, token),
        chatService.getConversations(token),
      ]);
      setMessages(msgs);
      setRooms(roomData);
    } catch (err) {
      console.error('Failed to send', err);
      setMessages(prev => prev.filter(m => m.id !== tempMsg.id));
    }
  };

  const handleDeleteMessage = (messageId) => {
    setMessageToDelete(messageId);
  };

  const confirmDeleteMessage = async () => {
    if (!messageToDelete) return;
    try {
      await chatService.deleteMessage(messageToDelete, token);
      setMessages(prev => prev.filter(m => m.id !== messageToDelete));
      if (activeRoom) {
        const roomData = await chatService.getConversations(token);
        setRooms(roomData);
      }
      setMessageToDelete(null);
    } catch (err) {
      console.error('Failed to delete message', err);
      setDeleteError('No tienes permiso para eliminar este recurso. Solo el remitente puede hacerlo.');
      setMessageToDelete(null);
    }
  };

  const handleLeaveGroup = () => {
    if (!activeRoom || activeRoom.type !== 'GROUP') return;
    setShowLeaveModal(true);
  };

  const confirmLeaveGroup = async () => {
    try {
      await chatService.leaveGroup(activeRoom.roomId, token);
      setActiveRoom(null);
      setMessages([]);
      setShowLeaveModal(false);
      const updatedRooms = await chatService.getConversations(token);
      setRooms(updatedRooms);
    } catch (err) {
      console.error('Error leaving group:', err);
      setShowLeaveModal(false);
      setDeleteError('No se pudo salir del grupo. Intenta nuevamente.');
    }
  };

  const onEmojiClick = (emojiData) => {
    setInput(prev => prev + emojiData.emoji);
    // Keep focus on input after selecting emoji (optional)
  };

  const handleInviteContact = async (contact) => {
    if (!activeRoom) return;
    setModalType(null);
    try {
      await chatService.addMembers(activeRoom.roomId, [contact.id], token);
      // Room might have changed from DIRECT to GROUP
      const updatedRooms = await chatService.getConversations(token);
      setRooms(updatedRooms);
      const newActive = updatedRooms.find(r => r.roomId === activeRoom.roomId);
      if (newActive) setActiveRoom(newActive);

      const freshMsgs = await chatService.getMessagesByRoom(activeRoom.roomId, token);
      setMessages(freshMsgs);
    } catch (err) {
      console.error('Failed to invite', err);
    }
  };

  const handleViewProfile = async (researcherId) => {
    setLoadingProfile(true);
    try {
      const detailedProfile = await userService.getResearcherProfile(researcherId, token);
      setViewingProfile(detailedProfile);
    } catch (err) {
      console.error('Error loading detailed profile:', err);
    } finally {
      setLoadingProfile(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const groupMessagesByDate = () => {
    const groups = [];
    let currentDate = '';
    messages.forEach(msg => {
      const date = formatDate(msg.createdAt);
      if (date !== currentDate) {
        currentDate = date;
        groups.push({ type: 'date', label: date });
      }
      groups.push({ type: 'message', data: msg });
    });
    return groups;
  };

  const quickContacts = contacts.slice(0, 5);
  const filteredRooms = rooms.filter(r =>
    !searchTerm || (r.name || r.user?.name || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  const sharedFiles = messages
    .filter(msg => msg.fileUrl && msg.fileUrl !== 'temp');

  return (
    <div className="mc-page">
      <Nav />
      <div className="mc-layout">

        {/* ── LEFT: Rooms ── */}
        <div className="mc-left">
          <div className="mc-left-header">
            <h2>Mensajes</h2>
            <button className="mc-new-chat-btn" onClick={() => setModalType('NEW')}>
              <FaEdit />
            </button>
          </div>

          {/* User Profile Info (Top of sidebar) */}
          <div className="mc-user-sidebar-profile">
            <Avatar
              name={`${user?.profile?.firstName || ''} ${user?.profile?.lastName || ''}`.trim() || 'Mi Perfil'}
              photoUrl={user?.profile?.photoUrl}
              size={48}
              className="mc-user-avatar-main"
            />
            <div className="mc-user-info-main">
              <p className="mc-user-name-main">{user?.profile?.firstName} {user?.profile?.lastName}</p>
              <p className="mc-user-status-main">En línea (Tú)</p>
            </div>
          </div>

          {quickContacts.length > 0 && (
            <div className="mc-quick-contacts">
              {quickContacts.map(c => (
                <div key={c.id} className="mc-quick-contact" onClick={() => handleSelectUser(c)}>
                  <Avatar name={c.name} photoUrl={c.photoUrl} size={44} className="mc-qc-avatar" />
                  <span>{c.name.split(' ')[0]}</span>
                </div>
              ))}
            </div>
          )}

          <div className="mc-search">
            <input
              type="text"
              placeholder="Buscar mensajes..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
            />
          </div>

          <div className="mc-conversations">
            {loading ? (
              <div className="mc-empty-chat"><p>Cargando...</p></div>
            ) : filteredRooms.length === 0 ? (
              <div className="mc-empty-chat">
                <FaComments style={{ fontSize: '2rem', opacity: 0.3 }} />
                <p>No hay conversaciones aún</p>
              </div>
            ) : (
              filteredRooms.map(room => (
                <div
                  key={room.roomId}
                  className={`mc-conv-item ${activeRoom?.roomId === room.roomId ? 'active' : ''}`}
                  onClick={() => handleSelectRoom(room)}
                >
                  <div className="mc-conv-avatar">
                    {room.type === 'GROUP' ? (
                      <div className="mc-avatar" style={{ backgroundColor: '#1f2128' }}>
                        <FaUsers style={{ color: '#FF6600' }} />
                      </div>
                    ) : (
                      <Avatar
                        name={room.user?.name || (room.user?.profile ? `${room.user.profile.firstName} ${room.user.profile.lastName}` : 'Usuario')}
                        photoUrl={room.user?.photoUrl || room.user?.profile?.photoUrl}
                        size={44}
                        className="mc-avatar"
                      />
                    )}
                    <div className="mc-online-indicator" />
                  </div>
                  <div className="mc-conv-info">
                    <p className="mc-conv-name">
                      {room.name || room.user?.name || (room.user?.profile ? `${room.user.profile.firstName} ${room.user.profile.lastName}` : 'Usuario')}
                    </p>
                    <p className="mc-conv-preview">
                      {room.lastMessage
                        ? (room.lastMessageSenderId === user?.id ? 'Tú: ' : '') + room.lastMessage
                        : room.type === 'GROUP' ? 'Chat grupal' : room.user?.role}
                    </p>
                  </div>
                  <div className="mc-conv-meta">
                    {room.lastMessageAt && <span className="mc-conv-time">{formatConvTime(room.lastMessageAt)}</span>}
                    {room.unreadCount > 0 && <span className="mc-conv-badge">{room.unreadCount}</span>}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* ── CENTER: Chat ── */}
        <div className="mc-center">
          {activeRoom ? (
            <>
              <div className="mc-chat-header">
                {activeRoom.type === 'GROUP' ? (
                  <div className="mc-chat-header-avatar" style={{ backgroundColor: '#1f2128' }}>
                    <FaUsers style={{ color: '#FF6600' }} />
                  </div>
                ) : (
                  <Avatar name={activeRoom.user?.name} photoUrl={activeRoom.user?.photoUrl} size={40} className="mc-chat-header-avatar" />
                )}
                <div className="mc-chat-header-info">
                  <p className="mc-chat-header-name">{activeRoom.name || activeRoom.user?.name}</p>
                  <p className="mc-chat-header-status">
                    {activeRoom.type === 'GROUP' ? `${activeRoom.members?.length} miembros` : 'En línea'}
                  </p>
                </div>
                <div className="mc-chat-header-actions">
                  <button title="Videollamada" onClick={() => setIsVideoCallActive(true)}><FaVideo /></button>
                  <button title="Invitar" onClick={() => setModalType('INVITE')}><FaUserPlus /></button>
                  <button title="Más opciones"><FaEllipsisV /></button>
                </div>
              </div>

              <div className="mc-messages">
                {groupMessagesByDate().map((item, idx) =>
                  item.type === 'date' ? (
                    <div key={`date-${idx}`} className="mc-date-divider">{item.label}</div>
                  ) : (
                    <div key={item.data.id} className={`mc-msg-wrapper ${item.data.sender?.id === user?.id ? 'sent' : 'received'}`}>
                      <Avatar
                        name={item.data.sender?.profile?.firstName || item.data.sender?.name || 'Usuario'}
                        photoUrl={item.data.sender?.profile?.photoUrl || item.data.sender?.photoUrl}
                        size={30}
                        className="mc-msg-avatar-small"
                      />
                      <div>
                        {activeRoom.type === 'GROUP' && item.data.sender?.id !== user?.id && (
                          <p style={{ fontSize: '0.65rem', color: '#FF6600', marginBottom: '2px', fontWeight: 'bold' }}>
                            {item.data.sender?.profile?.firstName}
                          </p>
                        )}
                        <div className={`mc-msg-bubble ${isOnlyEmojis(item.data.content) ? 'mc-jumbo-emoji-bubble' : ''}`}>
                          {item.data.fileUrl && (
                            <div className="mc-msg-file">
                              {item.data.fileType?.startsWith('image/') ? (
                                <img
                                  src={`${getBackendUrl()}${item.data.fileUrl}`}
                                  alt={item.data.fileName}
                                  className="mc-msg-image"
                                />
                              ) : (
                                <a href={`${getBackendUrl()}${item.data.fileUrl}`} target="_blank" rel="noopener noreferrer" className="mc-msg-file-link">
                                  <FaFile className="mc-file-icon" />
                                  <span>{item.data.fileName || 'Archivo adjunto'}</span>
                                </a>
                              )}
                            </div>
                          )}
                          {item.data.content && <div>{item.data.content}</div>}
                        </div>
                        <div className="mc-msg-time">
                          {formatTime(item.data.createdAt)}
                          {item.data.sender?.id === user?.id && (
                            <span className="mc-msg-check">{item.data.read ? <FaCheckDouble /> : <FaCheck />}</span>
                          )}
                        </div>
                      </div>
                    </div>
                  )
                )}
                <div ref={messagesEndRef} />
              </div>

              <div className="mc-input-area">
                <div className="mc-input-actions" ref={emojiPickerRef}>
                  <button
                    title="Emoji"
                    onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                    className={showEmojiPicker ? 'active' : ''}
                  >
                    <FaSmile />
                  </button>
                  {showEmojiPicker && (
                    <div className="mc-emoji-picker-container">
                      <EmojiPicker
                        onEmojiClick={onEmojiClick}
                        theme={Theme.DARK}
                        lazyLoadEmojis={true}
                        searchPlaceholder="Buscar emoji..."
                        width={320}
                        height={400}
                      />
                    </div>
                  )}
                  <button title="Adjuntar" onClick={() => fileInputRef.current?.click()}><FaPaperclip /></button>
                  <input
                    type="file"
                    ref={fileInputRef}
                    style={{ display: 'none' }}
                    onChange={handleFileChange}
                  />
                </div>

                {selectedFile && (
                  <div className="mc-file-preview-pill">
                    <FaFile className="mc-file-icon" />
                    <span className="mc-file-name">{selectedFile.name}</span>
                    <button className="mc-file-remove" onClick={clearFile}><FaTimes /></button>
                  </div>
                )}

                <input
                  className="mc-input-field"
                  type="text"
                  placeholder="Escribe un mensaje..."
                  value={input}
                  onChange={e => setInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                />
                <button className="mc-send-btn" onClick={handleSend} disabled={!input.trim() && !selectedFile}><FaPaperPlane /></button>
              </div>
            </>
          ) : (
            <div className="mc-empty-chat">
              <FaComments style={{ fontSize: '3rem', opacity: 0.2 }} />
              <p>Selecciona una conversación para comenzar</p>
            </div>
          )}
        </div>

        {/* ── RIGHT: Members & Info ── */}
        <div className="mc-right">
          {activeRoom ? (
            <>
              <div className="mc-contact-profile">
                {activeRoom.type === 'GROUP' ? (
                  <div className="mc-contact-avatar-large" style={{ backgroundColor: '#1f2128' }}>
                    <FaUsers size={40} style={{ color: '#FF6600' }} />
                  </div>
                ) : (
                  <Avatar
                    name={activeRoom.user?.name}
                    photoUrl={activeRoom.user?.photoUrl}
                    size={90}
                    className="mc-contact-avatar-large"
                  />
                )}
                <p className="mc-contact-name">{activeRoom.name || activeRoom.user?.name}</p>
                <p className="mc-contact-role">{activeRoom.type === 'GROUP' ? 'Conversación grupal' : activeRoom.user?.role}</p>

                {activeRoom.type === 'DIRECT' && (
                  <button
                    className="mc-view-profile-btn"
                    onClick={() => handleViewProfile(activeRoom.user.id)}
                    disabled={loadingProfile}
                  >
                    {loadingProfile ? 'Cargando...' : 'Ver Perfil Completo'}
                  </button>
                )}
              </div>

              {activeRoom.type === 'DIRECT' && activeRoom.user && (
                <div className="mc-contact-section">
                  <p className="mc-contact-section-title">Información de contacto</p>
                  <div className="mc-info-list">
                    <div className="mc-info-item">
                      <FaEnvelope className="mc-info-icon" />
                      <div>
                        <p className="mc-info-label">Correo Electrónico</p>
                        <p className="mc-info-value">{activeRoom.user.email || 'No especificado'}</p>
                      </div>
                    </div>
                    <div className="mc-info-item">
                      <FaMapMarkerAlt className="mc-info-icon" />
                      <div>
                        <p className="mc-info-label">País / Ubicación</p>
                        <p className="mc-info-value">{activeRoom.user.country || 'No especificado'}</p>
                      </div>
                    </div>
                    <div className="mc-info-item">
                      <FaUsers className="mc-info-icon" />
                      <div>
                        <p className="mc-info-label">Institución</p>
                        <p className="mc-info-value">{activeRoom.user.institution || 'No especificado'}</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div className="mc-contact-section">
                <p className="mc-contact-section-title">Miembros ({activeRoom.members?.length})</p>
                <div className="mc-modal-list" style={{ overflow: 'visible' }}>
                  {activeRoom.members?.map(m => {
                    const isMe = m.id === user.id;
                    const memberName = isMe
                      ? `${user?.profile?.firstName || ''} ${user?.profile?.lastName || ''}`.trim() || 'Tú'
                      : m.name;
                    const memberPhoto = isMe ? user?.profile?.photoUrl : m.photoUrl;

                    return (
                      <div key={m.id} className="mc-modal-contact" style={{ padding: '8px 0' }}>
                        <Avatar name={memberName} photoUrl={memberPhoto} size={32} className="mc-avatar" />
                        <div className="mc-modal-contact-info">
                          <p className="mc-modal-contact-name" style={{ fontSize: '0.8rem' }}>{isMe ? 'Tú' : memberName}</p>
                          <p className="mc-modal-contact-role" style={{ fontSize: '0.7rem' }}>{isMe ? 'Investigador (Tú)' : m.role}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="mc-contact-section">
                <p className="mc-contact-section-title">Archivos Compartidos</p>
                <div className="mc-shared-files-grid">
                  {sharedFiles.length > 0 ? (
                    sharedFiles.map((file, idx) => (
                      <div key={idx} className="mc-shared-file-item">
                        {file.fileType?.startsWith('image/') ? (
                          <a href={`${getBackendUrl()}${file.fileUrl}`} target="_blank" rel="noopener noreferrer" style={{ display: 'block', width: '100%', height: '100%', position: 'relative', zIndex: 1 }}>
                            <img
                              src={`${getBackendUrl()}${file.fileUrl}`}
                              alt={file.fileName}
                              className="mc-shared-image-thumb"
                            />
                          </a>
                        ) : (
                          <a href={`${getBackendUrl()}${file.fileUrl}`} target="_blank" rel="noopener noreferrer" className="mc-shared-file-thumb" title={file.fileName} style={{ position: 'relative', zIndex: 1 }}>
                            <FaFile size={24} className="mc-file-icon" />
                            <span className="mc-shared-file-name">{file.fileName}</span>
                          </a>
                        )}
                        {file.sender && user && String(file.sender.id) === String(user.id) && (
                          <button
                            className="mc-shared-file-delete-btn"
                            onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleDeleteMessage(file.id); }}
                            title="Eliminar archivo"
                            style={{ zIndex: 10 }}
                          >
                            <FaTrash size={12} />
                          </button>
                        )}
                      </div>
                    ))
                  ) : (
                    <div className="mc-shared-file-empty">
                      <FaFile size={20} />
                      <p>No hay archivos aún</p>
                    </div>
                  )}
                </div>
              </div>

              <div className="mc-contact-section">
                <button className="mc-start-chat-btn" style={{ width: '100%', justifyContent: 'center' }} onClick={() => setModalType('INVITE')}>
                  <FaUserPlus /> Agregar más personas
                </button>
              </div>

              <button
                className="mc-block-btn"
                onClick={activeRoom.type === 'GROUP' ? handleLeaveGroup : null}
              >
                {activeRoom.type === 'GROUP' ? <FaBan /> : <FaBan />}
                {activeRoom.type === 'GROUP' ? ' Salir del grupo' : ' Bloquear contacto'}
              </button>
            </>
          ) : (
            <div className="mc-empty-right">Info de la sala</div>
          )}
        </div>
      </div>

      {modalType === 'NEW' && (
        <ContactModal contacts={contacts} onSelect={handleSelectUser} onClose={() => setModalType(null)} title="Nueva conversación" />
      )}
      {modalType === 'INVITE' && activeRoom && (
        <ContactModal
          contacts={contacts}
          onSelect={handleInviteContact}
          onClose={() => setModalType(null)}
          title="Invitar a la conversación"
          excludeIds={activeRoom.members?.map(m => m.id) || []}
        />
      )}

      {/* ── MODAL: Ver Perfil ── */}
      {viewingProfile && (
        <div className="mc-modal-overlay" onClick={() => setViewingProfile(null)}>
          <div className="mc-profile-modal" onClick={e => e.stopPropagation()}>
            <button className="mc-modal-close-top" onClick={() => setViewingProfile(null)}><FaTimes /></button>
            <div className="mc-profile-modal-content">
              <ResearcherProfileView researcher={viewingProfile} />
            </div>
          </div>
        </div>
      )}

      {messageToDelete && (
        <div className="mc-delete-modal-overlay">
          <div className="mc-delete-modal">
            <h3>¿Eliminar archivo?</h3>
            <p>Esta acción no se puede deshacer. Se eliminará para todos en la conversación.</p>
            <div className="mc-delete-modal-actions">
              <button className="mc-cancel-btn" onClick={() => setMessageToDelete(null)}>Cancelar</button>
              <button className="mc-confirm-btn" onClick={confirmDeleteMessage}>Eliminar</button>
            </div>
          </div>
        </div>
      )}

      {deleteError && (
        <div className="mc-delete-modal-overlay">
          <div className="mc-delete-modal">
            <h3>Acción denegada</h3>
            <p>{deleteError}</p>
            <div className="mc-delete-modal-actions">
              <button className="mc-cancel-btn" onClick={() => setDeleteError(null)}>Entendido</button>
            </div>
          </div>
        </div>
      )}

      {showLeaveModal && (
        <div className="mc-delete-modal-overlay">
          <div className="mc-delete-modal">
            <h3>¿Salir del grupo?</h3>
            <p>Ya no podrás leer los nuevos mensajes ni participar en esta conversación. El historial anterior permanecerá visible para los demás.</p>
            <div className="mc-delete-modal-actions">
              <button className="mc-cancel-btn" onClick={() => setShowLeaveModal(false)}>Cancelar</button>
              <button className="mc-confirm-btn" onClick={confirmLeaveGroup}>Sí, salir</button>
            </div>
          </div>
        </div>
      )}

      {/* ── MODAL: Videollamada (Jitsi) ── */}
      {isVideoCallActive && activeRoom && (
        <div className="mc-video-call-overlay">
          <div className="mc-video-call-container">
            <button className="mc-video-close-btn" onClick={() => setIsVideoCallActive(false)}>
              <FaTimes /> Cerrar Videollamada
            </button>
            <JitsiMeeting
              roomName={`EcoScienceInnova-Chat-${activeRoom.roomId}`}
              configOverwrite={{
                startWithAudioMuted: false,
                disableModeratorIndicator: true,
                startScreenSharing: false,
                enableEmailInStats: false,
                prejoinPageEnabled: false
              }}
              interfaceConfigOverwrite={{
                DISABLE_JOIN_LEAVE_NOTIFICATIONS: true,
              }}
              userInfo={{
                displayName: user?.profile ? `${user.profile.firstName} ${user.profile.lastName}` : 'Investigador',
                email: user?.email
              }}
              onReadyToClose={() => setIsVideoCallActive(false)}
              onApiReady={(externalApi) => {
                externalApi.addListener('readyToClose', () => {
                  setIsVideoCallActive(false);
                });
                externalApi.addListener('videoConferenceLeft', () => {
                  setIsVideoCallActive(false);
                });
              }}
              getIFrameRef={(iframeRef) => {
                iframeRef.style.height = '100%';
                iframeRef.style.width = '100%';
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default MessengerChat;
